import { jsx as _jsx } from "react/jsx-runtime";
/**
 * La sessione di turno, e la conferma d'identità.
 *
 * Il pezzo che conta è `esegui`: ogni gesto passa di qui, e se il backend
 * risponde «serve la conferma» la richiesta della password si apre da sé e —
 * data la password — **il gesto viene ritentato**. L'operatore non deve
 * ricominciare, e soprattutto non deve capire cosa è successo: ha premuto un
 * tasto, gli è stata chiesta la password, il gesto è avvenuto.
 *
 * Senza il ritentativo automatico la conferma sarebbe vissuta come un guasto, e
 * una misura di sicurezza che sembra un guasto è una misura che verrà aggirata.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { accedi as accediApi, accediConGoogle as accediConGoogleApi, confermaIdentita, confermaIdentitaConGoogle, ConfermaRichiesta, quandoScadeLaSessione, SessioneScaduta, } from './transport';
import { dimenticaSceltaGoogle } from './google';
import { statoSessione } from './state';
const Ctx = createContext(null);
export function FornitoreSessione({ children }) {
    // L'identità vive nello store (`stato.ts`), non qui: la stessa cosa la deve
    // poter leggere il router **fuori** da React, e finché stava in un `useState`
    // la sola strada era riaprire `localStorage` a mano da un'altra parte — con
    // la chiave e la forma del dato scritte una seconda volta.
    //
    // La sessione resta **condivisa fra banco e ufficio**, che stanno sulla
    // stessa origine (`/` e `/backoffice/`): un solo utente per volta sullo
    // stesso browser, ed è la spec, non una svista. Adesso «Esci» dall'una
    // spegne l'altra **subito**, senza aspettare un ricaricamento.
    const identita = statoSessione((s) => s.identita);
    const [confermaInCorso, setConfermaInCorso] = useState(null);
    // Il gesto rimasto in sospeso mentre si chiede la password, e la promessa di
    // chi lo aspetta: è ciò che permette di ritentarlo invece di farlo rifare.
    const sospeso = useRef(null);
    const salva = useCallback((i) => {
        const stato = statoSessione.getState();
        if (i)
            stato.salva(i);
        else
            stato.esci();
    }, []);
    // Anche una lettura può scoprire che la sessione è finita: quando succede si
    // torna all'accesso, invece di restare su una schermata che non funziona.
    useEffect(() => {
        quandoScadeLaSessione(() => salva(null));
    }, [salva]);
    const accedi = useCallback(async (login, password) => {
        salva(await accediApi(login, password));
    }, [salva]);
    const esci = useCallback(() => {
        // the workstation may pass to someone else: Google must not reopen by
        // itself the session of whoever just left
        dimenticaSceltaGoogle();
        salva(null);
    }, [salva]);
    const esegui = useCallback(async (gesto) => {
        if (!identita)
            throw new SessioneScaduta();
        try {
            return await gesto(identita.token);
        }
        catch (e) {
            if (e instanceof SessioneScaduta) {
                salva(null);
                throw e;
            }
            if (e instanceof ConfermaRichiesta) {
                /*
                 * Il ref del gesto sospeso è uno solo per tutta la sessione, quindi
                 * non può essere generico: tiene la forma comune, `EsitoComando`. Il
                 * cast è sicuro perché a risolvere quella promessa è **lo stesso
                 * gesto ritentato** — vedi `rispondiAllaConferma` — cioè la stessa
                 * funzione che qui ha tipo `T`. È il ref che non sa esprimerlo, non
                 * il codice che indovina.
                 */
                return new Promise((risolvi, rifiuta) => {
                    sospeso.current = { gesto, risolvi: risolvi, rifiuta };
                    setConfermaInCorso(e.message);
                });
            }
            throw e;
        }
    }, [identita, salva]);
    /**
     * The confirmation, however it is given: save the confirmed identity and
     * retry the suspended gesture. Password or Google only change how `nuova`
     * is obtained.
     */
    const confermaERitenta = useCallback(async (conferma) => {
        if (!identita)
            throw new SessioneScaduta();
        const nuova = await conferma(identita.token);
        salva(nuova);
        setConfermaInCorso(null);
        const inAttesa = sospeso.current;
        sospeso.current = null;
        if (!inAttesa)
            return;
        try {
            inAttesa.risolvi(await inAttesa.gesto(nuova.token));
        }
        catch (e) {
            inAttesa.rifiuta(e);
        }
    }, [identita, salva]);
    const rispondiAllaConferma = useCallback((password) => confermaERitenta((t) => confermaIdentita(t, password)), [confermaERitenta]);
    const rispondiAllaConfermaConGoogle = useCallback((credenziale) => confermaERitenta((t) => confermaIdentitaConGoogle(t, credenziale)), [confermaERitenta]);
    const accediConGoogle = useCallback(async (credenziale) => {
        salva(await accediConGoogleApi(credenziale));
    }, [salva]);
    const annullaConferma = useCallback(() => {
        setConfermaInCorso(null);
        const inAttesa = sospeso.current;
        sospeso.current = null;
        // Il gesto non è avvenuto, e va detto: restituire un rifiuto invece di
        // lasciare la promessa appesa per sempre.
        inAttesa?.risolvi({
            ok: false,
            titolo: 'Gesto annullato',
            motivo: 'Serviva la conferma della tua identità e non è stata data.',
            regola: '—',
        });
    }, []);
    const valore = useMemo(() => ({
        identita,
        accedi,
        esci,
        esegui,
        confermaInCorso,
        rispondiAllaConferma,
        accediConGoogle,
        rispondiAllaConfermaConGoogle,
        annullaConferma,
    }), [
        identita,
        accedi,
        esci,
        esegui,
        confermaInCorso,
        rispondiAllaConferma,
        accediConGoogle,
        rispondiAllaConfermaConGoogle,
        annullaConferma,
    ]);
    return _jsx(Ctx.Provider, { value: valore, children: children });
}
export function useSessione() {
    const c = useContext(Ctx);
    if (!c)
        throw new Error('useSessione fuori dal FornitoreSessione');
    return c;
}
//# sourceMappingURL=provider.js.map